import {Injectable} from '@angular/core';
import {MmProjectService} from '../mm-project/mm-project.service';
import {BimModelApiService, BimModelSelectionService} from '@mydocma/co/src/lib/common';
import {BehaviorSubject, firstValueFrom, merge, switchMap} from 'rxjs';
import {BimModel} from '@mydocma/co/src/lib/common';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ProjectStructureNode, ProjectStructureService, ProjectStructureTree} from '@mydocma/project';
import {ExtendedBimModel} from './extended-bim-model';
import { CoEventService } from '@mydocma/co';


export interface BimModelState {


  bimModel: BimModel;

}

@UntilDestroy()
@Injectable()
export class BimModelAvailabilityService {







  private bimModelsSubject = new BehaviorSubject<ExtendedBimModel[]>(null);
  public bimModels$ = this.bimModelsSubject.asObservable();

  private isBimIntegraionEnabledSubject = new BehaviorSubject<boolean>(null);
  public isBimIntegrationEnabled$ = this.isBimIntegraionEnabledSubject.asObservable();

  private structureBimModelsSubject = new BehaviorSubject<BimModel[]>(null);
  public structureBimModels$ = this.structureBimModelsSubject.asObservable();


  get structureBimModels(): ExtendedBimModel[] {
    return this.structureBimModelsSubject.value;
  }

  get isBimModelEnabled(): boolean {
    return this.isBimIntegraionEnabledSubject.value;
  }

  // implemte rewset iof project is null

  constructor(
    private projectService: MmProjectService,
    private bimModelApiService: BimModelApiService,
    private projectStructureService: ProjectStructureService,
    private coEventService: CoEventService
  ) {

    this.bimModels$
      .pipe(switchMap((bimModels) => this.projectStructureService.currentProjectStructure$))
      .subscribe((structure: ProjectStructureNode) => this.onProjectStructureChange(structure));

    merge(
      this.projectService.currentProject,
      this.coEventService.onBimModelCreated,
      this.coEventService.onBimModelDeleted,
      this.coEventService.onBimModelUpdated
    )
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateBimModels();
      });
  }

  private getModelsForStructureRecursive(
    structure: ProjectStructureNode | undefined,
  ): ExtendedBimModel[] {

    const allModels = this.bimModelsSubject.value || [];


    const filteredModels = allModels.filter(model => model.structureId === structure.id);

    if (filteredModels.length > 0) {
      return filteredModels;
    }


    const parentStructure = structure?.parent;

    if (!parentStructure) {
      return [];
    }
    return this.getModelsForStructureRecursive(parentStructure);
  }


  private onProjectStructureChange(structure: ProjectStructureNode | undefined) {
    let modelsToUse: ExtendedBimModel[] = [];

    if (!structure) {
      modelsToUse = [];
    } else {
      const allModels = this.bimModelsSubject.value || [];
      if (!structure.parent) {
        modelsToUse = allModels;
      } else {
        const filteredModels = this.getModelsForStructureRecursive(structure);
        if (filteredModels.length) {
          modelsToUse = filteredModels;
        } else {
          const descendantModels = this.getModelsFromDescendants(structure, []);
          modelsToUse = descendantModels.length ? descendantModels : [];
        }
      }
    }

    this.structureBimModelsSubject.next(modelsToUse);
  }

  private getModelsFromDescendants(
    structure: ProjectStructureNode,
    gatheredModels: ExtendedBimModel[] = []
  ): ExtendedBimModel[] {
    const allModels = this.bimModelsSubject.value || [];

    const modelsForCurrent = allModels.filter(model => model.structureId === structure.id);
    gatheredModels.push(...modelsForCurrent);

    if (structure.children && structure.children.length > 0) {
      for (const child of structure.children) {
        this.getModelsFromDescendants(child, gatheredModels);
      }
    }

    return gatheredModels;
  }

  private async updateBimModels() {
      const currentProject = await firstValueFrom(this.projectService.currentProject);
  
      if (!currentProject) {
        console.warn('updateBimModels aborted: no current project');
        return;
      }
    
    const bimModels = await firstValueFrom(this.bimModelApiService.getBimModels());
    const buildHierarchicalBimModels = await this.buildHierarchicalBimModels(bimModels);
    this.bimModelsSubject.next(buildHierarchicalBimModels);
  }

  private async buildHierarchicalBimModels(bimModels: BimModel[]): Promise<ExtendedBimModel[]> {
    const bimModelMap = new Map<string, ExtendedBimModel>();
    const rootBimModels: ExtendedBimModel[] = [];
    const pendingChildrenMap = new Map<string, ExtendedBimModel[]>();
    let projectBimModelEnabled = false;

    for (const bimModel of bimModels) {
      if (bimModel.disabled) {
        continue;
      }

      // check if it`s root model
      if (!bimModel.parentBimModelId) {
        projectBimModelEnabled = true;
      }

      const extendedBimModel: ExtendedBimModel = bimModelMap.get(bimModel.id) || {
        ...bimModel,
        subModels: [],
      };

      bimModelMap.set(bimModel.id, extendedBimModel);


      if (bimModel.parentBimModelId) {
        const parentBimModel = bimModelMap.get(bimModel.parentBimModelId);

        if (parentBimModel && !parentBimModel.disabled) {
          if (!parentBimModel.subModels.includes(extendedBimModel)) {
            parentBimModel.subModels.push(extendedBimModel);
          }
        } else {
          let pendingChildrenForParent = pendingChildrenMap.get(bimModel.parentBimModelId);
          if (!pendingChildrenForParent) {
            pendingChildrenForParent = [];
            pendingChildrenMap.set(bimModel.parentBimModelId, pendingChildrenForParent);
          }
          if (!pendingChildrenForParent.includes(extendedBimModel)) {
            pendingChildrenForParent.push(extendedBimModel);
          }
        }
      } else {
        // No parent; current model is a root
        if (!rootBimModels.includes(extendedBimModel)) {
          rootBimModels.push(extendedBimModel);
        }
      }

// Check for pending children
      const pendingChildrenForCurrent = pendingChildrenMap.get(bimModel.id);
      if (pendingChildrenForCurrent) {
        for (const child of pendingChildrenForCurrent) {
          if (!extendedBimModel.subModels.includes(child)) {
            extendedBimModel.subModels.push(child);
          }
        }
        pendingChildrenMap.delete(bimModel.id);
      }
    }

    // Update the isBimIntegrationEnabledSubject here
    this.isBimIntegraionEnabledSubject.next(projectBimModelEnabled);

    return rootBimModels;
  }


}
